import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Plane, LogOut, CheckCircle2, XCircle, Send, CalendarCheck, Trophy, ClipboardList,
  ChevronDown, ExternalLink, MessageSquareText,
} from "lucide-react";

const statusLabel = {
  evaluated: "Not applied yet", applied: "Applied", skipped: "Skipped",
  rejected: "Rejected", interview: "Interview", offer: "Offer",
};
const statusStyle = {
  evaluated: "bg-slate-800 text-slate-400 border-slate-700",
  applied: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  skipped: "bg-slate-800 text-slate-400 border-slate-700",
  rejected: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  interview: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  offer: "bg-emerald-400/20 text-emerald-300 border-emerald-400/40",
};

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [client, setClient] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get("/evaluations").then((r) => setRows(r.data));
    api.get("/clients").then((r) => setClient(r.data[0] || null));
  }, []);

  const counts = {
    evaluated: rows.length,
    applied: rows.filter((r) => ["applied", "interview", "offer", "rejected"].includes(r.status)).length,
    interviews: rows.filter((r) => r.status === "interview").length,
    offers: rows.filter((r) => r.status === "offer").length,
  };

  return (
    <div className="min-h-screen bg-[#090A0F] grain-overlay">
      {/* Top bar */}
      <header className="border-b border-slate-800/70 bg-[#0D0E15] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Plane className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-slate-100">ApplyPilot</div>
            <div className="text-[11px] text-slate-500">Client portal</div>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <span className="text-sm text-slate-300">{user?.name}</span>
            <button data-testid="portal-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-rose-400 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6 animate-fade-up">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-100 tracking-tight">Your applications</h1>
          <p className="text-slate-400 text-sm mt-1">
            Every job your ApplyPilot team reviewed for you — the conclusion, why, and whether it was applied to.
          </p>
        </div>

        {client?.profile_summary && (
          <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5">
            <div className="text-[11px] uppercase tracking-widest text-indigo-400 font-bold mb-1.5">Your profile summary</div>
            <p className="text-sm text-slate-300 leading-relaxed">{client.profile_summary}</p>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            [ClipboardList, "Reviewed", counts.evaluated, "text-blue-400"],
            [Send, "Applied", counts.applied, "text-purple-400"],
            [CalendarCheck, "Interviews", counts.interviews, "text-amber-400"],
            [Trophy, "Offers", counts.offers, "text-emerald-400"],
          ].map(([Icon, label, value, color]) => (
            <div key={label} className="bg-[#12151F] border border-slate-800/80 rounded-xl px-5 py-4">
              <Icon className={`w-5 h-5 ${color}`} />
              <div className={`font-mono font-bold text-2xl mt-2 ${color}`}>{value}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          {rows.map((r) => {
            const open = openId === r.id;
            const apply = r.decision === "APPLY";
            return (
              <div key={r.id} data-testid={`portal-row-${r.id}`} className="bg-[#12151F] border border-slate-800/80 rounded-xl overflow-hidden">
                <button onClick={() => setOpenId(open ? null : r.id)} className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-slate-800/25 transition-colors">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${apply ? "bg-emerald-500/15" : "bg-rose-500/15"}`}>
                    {apply ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-rose-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-100 truncate">{r.job_title}</div>
                    <div className="text-xs text-slate-500 truncate">{r.company} · {r.location || "—"}</div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className={`font-mono font-bold ${apply ? "text-emerald-400" : "text-rose-400"}`}>{r.fit_score}</div>
                    <div className="text-[10px] text-slate-500 uppercase">fit</div>
                  </div>
                  <span className={`text-[11px] border px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${statusStyle[r.status] || statusStyle.evaluated}`}>
                    {statusLabel[r.status] || r.status}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>

                {open && (
                  <div className="px-5 pb-5 pt-1 border-t border-slate-800/50 space-y-3">
                    <div className={`inline-flex items-center gap-2 text-sm font-bold ${apply ? "text-emerald-400" : "text-rose-400"}`}>
                      {r.decision}
                      <span className="text-xs font-normal text-slate-400">
                        · {apply ? "recommended to apply" : "recommended to skip"}
                      </span>
                    </div>
                    {r.reasons?.length > 0 && (
                      <div className="bg-[#0D0E15] border border-slate-800 rounded-lg p-3">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Why</div>
                        <ul className="space-y-1 text-sm text-slate-300">
                          {r.reasons.map((x, i) => <li key={i}>• {x}</li>)}
                        </ul>
                      </div>
                    )}
                    {r.dealbreakers?.length > 0 && (
                      <div className="text-xs bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 text-rose-300">
                        Dealbreaker: {r.dealbreakers.join("; ")}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>Sponsorship: <span className="text-slate-300">{r.sponsorship}</span></span>
                      {r.salary && <span>Salary: <span className="text-slate-300">{r.salary}</span></span>}
                      {r.job_url && <a href={r.job_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300">View posting <ExternalLink className="w-3 h-3" /></a>}
                    </div>
                    {r.notes && <div className="text-xs text-slate-400">Note from your executive: {r.notes}</div>}
                    {(r.qa || []).length > 0 && (
                      <div data-testid={`portal-qa-${r.id}`} className="bg-[#0D0E15] border border-slate-800 rounded-lg p-3 space-y-3">
                        <div className="text-[10px] uppercase tracking-wider text-indigo-400 font-semibold flex items-center gap-1.5">
                          <MessageSquareText className="w-3.5 h-3.5" /> Application answers
                        </div>
                        {(r.qa || []).map((q) => (
                          <div key={q.id} data-testid={`portal-qa-item-${q.id}`} className="border-l-2 border-indigo-500/40 pl-3">
                            <div className="text-sm font-semibold text-indigo-300">{q.question}</div>
                            <div className="text-sm text-slate-300 mt-1 whitespace-pre-wrap leading-relaxed">{q.answer}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="text-center text-slate-500 py-16 text-sm bg-[#12151F] border border-slate-800/80 rounded-xl">
              No jobs have been reviewed for you yet. Your ApplyPilot team will start logging them here.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
